/**
 * Conversation pane for the plugin builder.
 * Displays chat messages, streaming text, verbose events, and guided flow panels.
 */

import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useAutoScroll } from '@/lib/hooks/useAutoScroll';
import type { VerboseEvent } from '../types';
import { VerboseTimeline } from '../../../components/VerboseTimeline';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface PluginConversationPaneProps {
  messages: Message[];
  isProcessing: boolean;
  streamingText: string;
  emptyStateContent?: React.ReactNode;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  // Verbose events
  verboseHistory?: Record<string, VerboseEvent[]>;
  pendingVerboseEvents?: VerboseEvent[];
  isAdmin?: boolean;
  // Suggested next steps
  suggestedNextSteps?: string[];
  onSuggestionClick?: (suggestion: string) => void;
  className?: string;
}

function MessageBubble({ message, isStreaming }: { message: Message; isStreaming?: boolean }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-4 py-3',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
        )}
      >
        <p className="whitespace-pre-wrap text-sm">
          {message.content}
          {isStreaming && <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse" />}
        </p>
      </div>
    </div>
  );
}

export function PluginConversationPane({
  messages,
  isProcessing,
  streamingText,
  emptyStateContent,
  headerContent,
  footerContent,
  verboseHistory,
  pendingVerboseEvents,
  isAdmin = false,
  suggestedNextSteps,
  onSuggestionClick,
  className,
}: PluginConversationPaneProps) {
  const { containerRef, anchorRef, scrollToBottomIfNeeded } = useAutoScroll();

  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [messages, streamingText, scrollToBottomIfNeeded]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {headerContent}
      <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          emptyStateContent ? (
            emptyStateContent
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-muted-foreground">
                Describe what you want to build below.
              </p>
            </div>
          )
        ) : (
          messages.map((message, index) => {
            // Show verbose events after each assistant message (if admin)
            const messageKey = message.timestamp || String(index);
            const events = isAdmin && verboseHistory ? verboseHistory[messageKey] || [] : [];

            return (
              <div key={index}>
                <MessageBubble message={message} />
                {events.length > 0 && <VerboseTimeline events={events} />}
              </div>
            );
          })
        )}

        {/* Streaming message */}
        {isProcessing &&
          (streamingText ? (
            <div>
              <MessageBubble message={{ role: 'assistant', content: streamingText }} isStreaming />
              {isAdmin && pendingVerboseEvents && pendingVerboseEvents.length > 0 && (
                <VerboseTimeline events={pendingVerboseEvents} isPending />
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Fias AI is thinking...</span>
              </div>
              {isAdmin && pendingVerboseEvents && pendingVerboseEvents.length > 0 && (
                <VerboseTimeline events={pendingVerboseEvents} isPending />
              )}
            </div>
          ))}

        {/* Suggested next steps */}
        {!isProcessing &&
          suggestedNextSteps &&
          suggestedNextSteps.length > 0 &&
          onSuggestionClick && (
            <div className="flex flex-wrap gap-2 pt-2">
              {suggestedNextSteps.map((step, i) => (
                <button
                  key={i}
                  onClick={() => onSuggestionClick(step)}
                  className="px-3 py-1.5 text-xs rounded-full border bg-background hover:bg-muted transition-colors"
                >
                  {step}
                </button>
              ))}
            </div>
          )}
        <div ref={anchorRef} />
      </div>
      {footerContent}
    </div>
  );
}
